import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Scissors, CheckCircle2 } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ResetPasswordPage() {
  const q = useQuery();
  const nav = useNavigate();
  const token = q.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing or invalid reset token."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      setOk(true);
      setTimeout(() => nav("/login"), 1800);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FAFAFA] text-[#0A0A0A]" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Scissors size={20} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          <div className="font-serif text-xl tracking-widest">MMS HUB CENTER</div>
        </div>
        <div className="border border-[#E5E5E5] p-8 bg-[#FFFFFF]">
          {!ok ? (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="label-caps text-[#0A0A0A] mb-3">Set a new password</div>
              <h2 className="font-serif text-3xl font-light mb-6">Reset password</h2>

              <label className="label-caps block mb-2">New password</label>
              <input
                data-testid="reset-password"
                type="password" required minLength={6} autoFocus
                value={password} onChange={(e)=>setPassword(e.target.value)}
                className="input-dark mb-5"
              />

              <label className="label-caps block mb-2">Confirm password</label>
              <input
                data-testid="reset-password-confirm"
                type="password" required minLength={6}
                value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                className="input-dark mb-5"
              />

              {err && <div className="text-sm text-red-600 mb-4" data-testid="reset-err">{err}</div>}

              <button
                data-testid="reset-submit"
                disabled={loading}
                className="btn-gold w-full disabled:opacity-50"
              >
                {loading ? "Updating…" : "Set new password"}
              </button>
              <div className="text-center text-xs text-[#737373] mt-6">
                <Link to="/login" className="text-[#0A0A0A]" data-testid="reset-back-link">Back to sign in</Link>
              </div>
            </form>
          ) : (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 size={28} className="text-green-400 mb-4 mx-auto" strokeWidth={1.5}/>
              <div className="label-caps text-[#0A0A0A] mb-3">Done</div>
              <h2 className="font-serif text-3xl font-light mb-4">Password updated</h2>
              <p className="text-sm text-[#525252] mb-4">
                Your password has been reset. Redirecting to sign in…
              </p>
              <Link to="/login" className="text-xs text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest">Sign in now</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
